import React, { useEffect, useState, useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import productData from "../data/canon-products.json";

function PrinterSlider() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const filtered = productData.filter(
      (item) =>
        item.category &&
        (item.category.toLowerCase().includes("home-printer") ||
          item.category.toLowerCase().includes("inkjet-printer"))
    );
    setProducts(filtered);
  }, []);

  const formatCategory = (str) =>
    str.replace(/[-_]/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  const productsWithData = useMemo(() => {
    return products.map((item) => ({
      ...item,
      rating: (Math.random() * 1 + 4).toFixed(1),
      reviews: Math.floor(Math.random() * 500) + 50,
      formattedCategory: formatCategory(item.category || "Printer"),
    }));
  }, [products]);

  const handleAddToCart = (product) => {
    const isLoggedIn = sessionStorage.getItem("isLoggedIn");

    if (isLoggedIn !== "true") {
      toast.error("Please login first!");
      navigate("/login-now");
      return;
    }

    let cart = JSON.parse(sessionStorage.getItem("cart")) || [];
    const existing = cart.find((item) => item.id === product.id);

    if (existing) {
      existing.quantity++;
    } else {
      cart.push({ ...product, quantity: 1 });
    }

    sessionStorage.setItem("cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cart-updated"));

    toast.success(`${product.name} added to cart!`);
  };

  return (
    <section className="printer-slider-modern">
      <div className="ps-container">
        {/* HEADING */}
        <div className="ps-heading">
          <h2>Featured Printers for Every Home</h2>
          <p>
            Hand-picked models offering reliability, crisp print quality, and
            smart wireless features — perfect for everyday printing.
          </p>
        </div>

        {/* SLIDER */}
        <Swiper
          spaceBetween={26}
          grabCursor={true}
          breakpoints={{
            0: { slidesPerView: 1 },
            576: { slidesPerView: 1 },
            768: { slidesPerView: 2 },
            1200: { slidesPerView: 3 },
            1400: { slidesPerView: 4 },
          }}
          loop={productsWithData.length > 4}
          className="ps-swiper"
        >
          {productsWithData.map((item) => {
            const img =
              item.image || item.imageUrl || "/assets/images/placeholder.png";
            const finalPrice = item.salePrice || item.price;

            return (
              <SwiperSlide key={item.id}>
                <div className="ps-card">
                  <div className="ps-img-box">
                    <img src={img} alt={item.name} />
                  </div>

                  <div className="ps-content">
                    <h3>
                      <Link to={`/product-details/${item.id}`}>
                        {item.name}
                      </Link>
                    </h3>

                    <div className="ps-rating">
                      ⭐ {item.rating} <span>({item.reviews})</span>
                    </div>

                    <div className="ps-price">${finalPrice}</div>

                    <div className="ps-meta">
                      <i className="fa-light fa-tags" />
                      {item.formattedCategory}
                    </div>

                    <button
                      className="ps-btn"
                      onClick={() => handleAddToCart(item)}
                    >
                      Add to Cart
                    </button>
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>

      {/* === LOCAL SCOPED CSS === */}
      <style>{`
        .printer-slider-modern {
          padding: 90px 0;
          background: #fff;
        }

        .printer-slider-modern .ps-container {
          max-width: 1250px;
          margin: auto;
          padding: 0 20px;
        }

        /* Heading */
        .printer-slider-modern .ps-heading {
          text-align: center;
          margin-bottom: 40px;
        }

        .printer-slider-modern .ps-heading h2 {
          font-size: 34px;
          font-weight: 800;
          color: #0f172a;
          margin-bottom: 12px;
        }

        .printer-slider-modern .ps-heading p {
          font-size: 16px;
          color: #64748b;
          max-width: 620px;
          margin: auto;
        }

        /* Product Card */
        .ps-card {
          background: #ffffff;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          transition: 0.25s ease;
        }

        .ps-card:hover {
          transform: translateY(-8px);
          box-shadow: 0px 12px 28px rgba(0,0,0,0.08);
          border-color: #4C8BF5;
        }

        /* Image */
        .ps-img-box {
          background: #f1f5f9;
          padding: 24px;
          display: flex;
          justify-content: center;
          height: 190px;
        }

        .ps-img-box img {
          width: 75%;
          height: 100%;
          object-fit: contain;
          transition: 0.3s;
        }

        .ps-card:hover img {
          transform: scale(1.08);
        }

        /* Content */
        .ps-content {
          padding: 18px 20px 24px;
          text-align: left;
        }

        .ps-content h3 {
          margin: 0 0 8px;
          font-size: 18px;
          font-weight: 700;
        }

        .ps-content h3 a {
          text-decoration: none;
          color: #1e293b;
        }

        .ps-rating {
          font-size: 14px;
          color: #e59f0f;
          margin-bottom: 8px;
        }

        .ps-rating span {
          color: #64748b;
        }

        .ps-price {
          font-size: 22px;
          font-weight: 800;
          margin-bottom: 8px;
          color: #0b5ed7;
        }

        .ps-meta {
          font-size: 13px;
          color: #64748b;
          display: flex;
          gap: 6px;
          align-items: center;
          margin-bottom: 14px;
        }

        .ps-meta i {
          font-size: 14px;
        }

        /* Button */
        .ps-btn {
          width: 100%;
          background: #4C8BF5;
          color: #fff;
          border: none;
          padding: 12px;
          border-radius: 6px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: 0.25s ease;
        }

        .ps-btn:hover {
          background: #2364d8;
        }
      `}</style>
    </section>
  );
}

export default PrinterSlider;
